'use client'

import { useState, useEffect, useContext } from 'react'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/shadcn/table'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Loader2, AlertCircle } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'

export default function BillingHistory({ currentTier }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [invoices, setInvoices] = useState([])
  const authContext = useContext(AuthContext)

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const { data } = await axios(
          `/organization/${authContext.user.organization.id}/invoices`,
          { method: 'GET' },
          authContext,
          true, // Set Content-Type
          'billing', // Use Billing URL
        )
        setInvoices(data)
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to load invoice history',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchInvoices()
  }, [authContext])

  if (currentTier === 'Free') {
    return null
  }

  if (loading) {
    return (
      <div>
        <div className="vertically-centered">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-2">
        <AlertCircle className="size-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (invoices.length === 0) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="px-4">
          <h6 className="text-base font-semibold mb-2">Billing History</h6>
          <p className="text-sm text-muted-foreground">No invoices found</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="px-4">
        <h6 className="text-base font-semibold mb-2">Billing History</h6>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map(invoice => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    {new Date(invoice.created * 1000).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{invoice.number}</TableCell>
                  <TableCell className="text-right">
                    ${(invoice.amount / 100).toFixed(2)}{' '}
                    {invoice.currency.toUpperCase()}
                  </TableCell>
                  <TableCell className="text-right">
                    <a
                      href={invoice.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground hover:underline"
                    >
                      Download PDF
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
